import { logger } from 'packages/logger';
import TRANSACTION_TYPES from 'tradera-constants/transaction-types';
import ITEM_TYPES from 'tradera-constants/item-types';
import { ITEM_STATES } from './constants';

const getEndedItemState = (
    itemType,
    bidCount,
    reservePriceReached,
    openingBid,
    nextValidBidAmount,
    transactionType,
    leadingBidMemberId,
    isCancelled
) => {
    if (isCancelled) {
        return ITEM_STATES.ENDED_CANCELLED;
    }

    const isAuction = itemType === ITEM_TYPES.AUCTION;
    const isAuctionBin = itemType === ITEM_TYPES.AUCTION_BIN;
    const isBuyItNow = itemType === ITEM_TYPES.PURE_BIN;
    const isShopItem = itemType === ITEM_TYPES.SHOP_ITEM;

    // transactionType may not be set if feature-toggle 'always-enrich-with-transactiontype' is off in WebApi.
    if (transactionType === TRANSACTION_TYPES.BIN) {
        return ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN;
    }

    // When transactionType is not available (feature toggle is off), we can still figure out
    // that the item was bought with buy-it-now price if there are no bids and a leading bidder exist.
    if (
        !transactionType &&
        isAuctionBin &&
        bidCount === 0 &&
        reservePriceReached &&
        leadingBidMemberId > 0
    ) {
        return ITEM_STATES.ENDED_AUCTION_BIN_BOUGHT_AS_BIN;
    }

    if ((isAuction || isAuctionBin) && bidCount === 0) {
        return ITEM_STATES.ENDED_AUCTION_WITHOUT_BID;
    }
    if (bidCount > 0 && !reservePriceReached) {
        return ITEM_STATES.ENDED_AUCTION_WITHOUT_WINNING_BID;
    }
    if (bidCount > 0 && reservePriceReached) {
        return ITEM_STATES.ENDED_AUCTION_WITH_WINNING_BID;
    }
    if (isBuyItNow || isShopItem) {
        return ITEM_STATES.ENDED_FIXED_PRICE;
    }
    logger(
        `Unknown ended state for parameters: ${JSON.stringify({
            itemType,
            bidCount,
            reservePriceReached,
            openingBid,
            nextValidBidAmount
        })}`
    );
    return ITEM_STATES.UNKNOWN;
};

const getOngoingItemState = itemType => {
    if (itemType === ITEM_TYPES.AUCTION) {
        return ITEM_STATES.ONGOING_AUCTION;
    }
    if (itemType === ITEM_TYPES.AUCTION_BIN) {
        return ITEM_STATES.ONGOING_AUCTION_BIN;
    }
    if (itemType === ITEM_TYPES.PURE_BIN || itemType === ITEM_TYPES.SHOP_ITEM) {
        return ITEM_STATES.ONGOING_FIXED_PRICE;
    }
    logger(
        `Unknown ongoing state for parameters: ${JSON.stringify({
            itemType
        })}`
    );
    return ITEM_STATES.UNKNOWN;
};

export const getItemState = (
    hasEnded,
    itemType,
    bidCount,
    reservePriceReached,
    openingBid,
    nextValidBidAmount,
    transactionType,
    leadingBidMemberId,
    isCancelled
) => {
    if (hasEnded) {
        return getEndedItemState(
            itemType,
            bidCount,
            reservePriceReached,
            openingBid,
            nextValidBidAmount,
            transactionType,
            leadingBidMemberId,
            isCancelled
        );
    }
    return getOngoingItemState(itemType);
};
